<template>
  <div class="signature-canvas-wrapper" ref="wrapperRef">
    <svg
      ref="svgRef"
      :viewBox="`0 0 ${width} ${height}`"
      :width="width"
      :height="height"
      xmlns="http://www.w3.org/2000/svg"
      style="background: white; cursor: crosshair; touch-action: none; display: block; width: 100%;"
      @mousedown="onPointerDown"
      @mousemove="onPointerMove"
      @mouseup="onPointerUp"
      @mouseleave="onPointerUp"
      @touchstart.prevent="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend.prevent="onPointerUp"
    >
      <polyline
        v-for="(stroke, i) in strokes"
        :key="i"
        :points="stroke.map(p => `${p.x},${p.y}`).join(' ')"
        fill="none"
        stroke="#1a1a1a"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <polyline
        v-if="currentStroke.length > 0"
        :points="currentStroke.map(p => `${p.x},${p.y}`).join(' ')"
        fill="none"
        stroke="#1a1a1a"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  height: { type: Number, default: 200 },
})

const emit = defineEmits(['change'])

const wrapperRef = ref(null)
const svgRef    = ref(null)
const width     = ref(600)
const strokes   = ref([])
const currentStroke = ref([])
const isDrawing = ref(false)

const isEmpty = ref(true)

// ── Coordinate helpers ────────────────────────────────────────────────────

function getSvgPoint(clientX, clientY) {
  const rect = svgRef.value.getBoundingClientRect()
  const scaleX = width.value / rect.width
  const scaleY = props.height / rect.height
  return {
    x: +(((clientX - rect.left) * scaleX).toFixed(2)),
    y: +(((clientY - rect.top)  * scaleY).toFixed(2)),
  }
}

// ── Mouse handlers ────────────────────────────────────────────────────────

function onPointerDown(e) {
  isDrawing.value = true
  currentStroke.value = [getSvgPoint(e.clientX, e.clientY)]
}

function onPointerMove(e) {
  if (!isDrawing.value) return
  currentStroke.value.push(getSvgPoint(e.clientX, e.clientY))
}

function onPointerUp() {
  if (!isDrawing.value) return
  isDrawing.value = false
  if (currentStroke.value.length > 1) {
    strokes.value.push([...currentStroke.value])
    isEmpty.value = false
    emit('change', false)
  }
  currentStroke.value = []
}

// ── Touch handlers ────────────────────────────────────────────────────────

function onTouchStart(e) {
  if (e.touches.length !== 1) return
  const t = e.touches[0]
  isDrawing.value = true
  currentStroke.value = [getSvgPoint(t.clientX, t.clientY)]
}

function onTouchMove(e) {
  if (!isDrawing.value || e.touches.length !== 1) return
  const t = e.touches[0]
  currentStroke.value.push(getSvgPoint(t.clientX, t.clientY))
}

// ── Resize observer ───────────────────────────────────────────────────────

let resizeObserver = null
onMounted(() => {
  if (wrapperRef.value) {
    width.value = wrapperRef.value.offsetWidth || 600
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        width.value = entry.contentRect.width
      }
    })
    resizeObserver.observe(wrapperRef.value)
  }
})
onUnmounted(() => resizeObserver?.disconnect())

// ── Public API ────────────────────────────────────────────────────────────

function clear() {
  strokes.value = []
  currentStroke.value = []
  isEmpty.value = true
  emit('change', true)
}

function getImageData() {
  const svgEl = svgRef.value
  if (!svgEl) return null
  return 'data:image/svg+xml,' + encodeURIComponent(svgEl.outerHTML)
}

defineExpose({ clear, getImageData, isEmpty })
</script>
